import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import API from '../../utils/api';
import usePricing from '../../utils/usePricing';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const fmt = (amount, currency) =>
  `${currency} ${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const AdminRevenue = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const { pricing }           = usePricing();

  const P = {
    basicMonthly:  pricing.basic,
    familyMonthly: pricing.family,
    basicAnnual:   pricing.basicAnnual,
    familyAnnual:  pricing.familyAnnual,
    gift:          pricing.gift,
  };
  const tp = pricing.topUpPacks || {};

  useEffect(() => {
    API.get('/admin/revenue')
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <AdminLayout title="Revenue">
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border" style={{ color: '#6f42c1' }}></div>
      </div>
    </AdminLayout>
  );

  const {
    activeBasic, activeFamily, cancelledThisMonth,
    deliveredGifts, planBreakdown,
    mrr, totalGiftRevenue, monthlySignups, monthlyGifts,
    stripeRevenue,
  } = data;

  const churnRate = (activeBasic + activeFamily) > 0
    ? ((cancelledThisMonth / (activeBasic + activeFamily)) * 100).toFixed(1)
    : 0;

  // ── Stripe-sourced values ──────────────────────────────────────────────────
  const hasStripe = !!stripeRevenue;

  // Total MRR across all currencies (display as list)
  const mrrEntries = hasStripe
    ? Object.entries(stripeRevenue.mrrByCurrency).sort((a, b) => b[1] - a[1])
    : null;
  const mrrPrimary = mrrEntries?.[0]; // biggest currency by MRR

  // Monthly chart: Stripe balance transactions or fallback signups
  let chartData = []; // [{ key, label, amount, currency, count }]

  if (hasStripe) {
    // Find primary currency across all months (most total net)
    const currTotals = {};
    Object.values(stripeRevenue.monthlyRevenue).forEach(monthObj => {
      Object.entries(monthObj).forEach(([cur, d]) => {
        currTotals[cur] = (currTotals[cur] || 0) + d.net;
      });
    });
    const primaryCur = Object.entries(currTotals).sort((a,b) => b[1]-a[1])[0]?.[0] || 'USD';

    // Build 6-month range
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setDate(1);
      d.setMonth(d.getMonth() - i);
      months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }

    chartData = months.map(key => {
      const monthObj = stripeRevenue.monthlyRevenue[key] || {};
      const d        = monthObj[primaryCur] || { net: 0, gross: 0, fees: 0, count: 0 };
      const [y, m]   = key.split('-');
      // Build tooltip: all currencies this month
      const allCurrencies = Object.entries(monthObj)
        .map(([cur, v]) => `${cur} ${v.net.toFixed(2)}`)
        .join(' · ');
      return {
        key,
        label:    `${MONTH_NAMES[parseInt(m) - 1]} ${y}`,
        amount:   d.net,
        currency: primaryCur,
        count:    Object.values(monthObj).reduce((s, v) => s + v.count, 0),
        tooltip:  allCurrencies || '—',
      };
    });
  } else {
    // Fallback: estimate from MongoDB signups
    const map = {};
    monthlySignups.forEach(({ _id, count }) => {
      const key = `${_id.year}-${String(_id.month).padStart(2, '0')}`;
      const rev = _id.plan === 'basic' ? count * P.basicMonthly
                : _id.plan === 'family' ? count * P.familyMonthly : 0;
      map[key] = (map[key] || 0) + rev;
    });
    monthlyGifts.forEach(({ _id, count }) => {
      const key = `${_id.year}-${String(_id.month).padStart(2, '0')}`;
      map[key] = (map[key] || 0) + count * P.gift;
    });
    chartData = Object.keys(map).sort().map(key => {
      const [y, m] = key.split('-');
      return { key, label: `${MONTH_NAMES[parseInt(m) - 1]} ${y}`, amount: map[key], currency: 'USD', count: 0, tooltip: '' };
    });
  }

  const maxAmount = Math.max(...chartData.map(d => d.amount), 1);

  // This month summary
  const thisMonthEntries = hasStripe
    ? Object.entries(stripeRevenue.thisMonth).sort((a,b) => b[1].net - a[1].net)
    : null;

  return (
    <AdminLayout title="Revenue">

      {/* Stripe badge */}
      {hasStripe && (
        <div className="d-flex align-items-center gap-2 mb-3">
          <span className="badge rounded-pill px-3 py-2" style={{ background: '#635bff', color: '#fff', fontSize: '12px' }}>
            <i className="bi bi-check-circle-fill me-1"></i>Live Stripe Data
          </span>
          <span style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>All figures sourced directly from your Stripe account</span>
        </div>
      )}
      {!hasStripe && (
        <div className="alert alert-warning py-2 small mb-3">
          <i className="bi bi-exclamation-triangle me-1"></i>
          Stripe API unavailable — showing estimated figures based on user counts × USD prices.
        </div>
      )}

      {/* Top stat cards */}
      <div className="row g-3 mb-4">

        {/* MRR */}
        <div className="col-6 col-lg-3">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>💰</div>
              {hasStripe && mrrPrimary ? (
                <>
                  <div style={{ fontSize: '26px', fontWeight: 700, color: '#059669', lineHeight: 1 }}>
                    {fmt(mrrPrimary[1], mrrPrimary[0])}
                  </div>
                  {mrrEntries.length > 1 && (
                    <div style={{ fontSize: '11px', color: 'var(--text-soft)', marginTop: '4px' }}>
                      + {mrrEntries.slice(1).map(([c, v]) => `${c} ${v.toFixed(0)}`).join(' · ')}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ fontSize: '30px', fontWeight: 700, color: '#059669', lineHeight: 1 }}>
                  ${mrr.toFixed(2)}
                </div>
              )}
              <div style={{ fontSize: '13px', color: 'var(--text-muted2)', marginTop: '4px' }}>
                {hasStripe ? 'Real MRR (Stripe)' : 'Est. MRR'}
              </div>
              {hasStripe && mrrPrimary && (
                <div style={{ fontSize: '12px', color: 'var(--text-soft)' }}>
                  ARR ≈ {fmt(mrrPrimary[1] * 12, mrrPrimary[0])}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* This month */}
        <div className="col-6 col-lg-3">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>📅</div>
              {hasStripe && thisMonthEntries?.length > 0 ? (
                <>
                  {thisMonthEntries.map(([cur, d]) => (
                    <div key={cur}>
                      <div style={{ fontSize: '26px', fontWeight: 700, color: 'var(--text-main)', lineHeight: 1 }}>
                        {fmt(d.net, cur)}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-soft)' }}>
                        gross {fmt(d.gross, cur)} · fees {fmt(d.fees, cur)}
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div style={{ fontSize: '30px', fontWeight: 700, color: 'var(--text-main)', lineHeight: 1 }}>—</div>
              )}
              <div style={{ fontSize: '13px', color: 'var(--text-muted2)', marginTop: '4px' }}>
                {hasStripe ? 'This Month (Net)' : 'This Month'}
              </div>
              {hasStripe && thisMonthEntries?.length > 0 && (
                <div style={{ fontSize: '12px', color: 'var(--text-soft)' }}>
                  {thisMonthEntries.reduce((s,[,d]) => s + d.count, 0)} transactions
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Subscribers */}
        <div className="col-6 col-lg-3">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>👥</div>
              <div style={{ fontSize: '30px', fontWeight: 700, color: '#6f42c1', lineHeight: 1 }}>
                {activeBasic + activeFamily}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted2)', marginTop: '4px' }}>Active Subscribers</div>
              <div style={{ fontSize: '12px', color: 'var(--text-soft)' }}>
                {activeBasic} basic · {activeFamily} family
              </div>
            </div>
          </div>
        </div>

        {/* Stripe balance / Gift */}
        <div className="col-6 col-lg-3">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              {hasStripe && stripeRevenue.availableBalance.length > 0 ? (
                <>
                  <div style={{ fontSize: '28px', marginBottom: '8px' }}>🏦</div>
                  {stripeRevenue.availableBalance.map(b => (
                    <div key={b.currency} style={{ fontSize: '22px', fontWeight: 700, color: '#059669', lineHeight: 1.2 }}>
                      {fmt(b.amount, b.currency)}
                    </div>
                  ))}
                  <div style={{ fontSize: '13px', color: 'var(--text-muted2)', marginTop: '4px' }}>Available Balance</div>
                  {stripeRevenue.pendingBalance.map(b => (
                    <div key={b.currency} style={{ fontSize: '12px', color: 'var(--text-soft)' }}>
                      + {fmt(b.amount, b.currency)} pending
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div style={{ fontSize: '28px', marginBottom: '8px' }}>🎁</div>
                  <div style={{ fontSize: '30px', fontWeight: 700, color: '#d97706', lineHeight: 1 }}>
                    ${totalGiftRevenue.toFixed(2)}
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--text-muted2)', marginTop: '4px' }}>Gift Revenue</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-soft)' }}>{deliveredGifts} delivered orders</div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4">

        {/* Revenue bar chart */}
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between mb-1">
                <h6 className="fw-bold mb-0" style={{ color: 'var(--text-main)' }}>
                  {hasStripe ? 'Monthly Revenue (Net, from Stripe)' : 'Estimated Monthly Revenue'}
                </h6>
                {hasStripe && chartData[0]?.currency && (
                  <span className="badge rounded-pill" style={{ background: 'var(--purple-section)', color: '#6f42c1', fontSize: '11px' }}>
                    {chartData[0].currency}
                  </span>
                )}
              </div>
              <p className="text-muted small mb-4">
                {hasStripe
                  ? 'Net revenue after Stripe fees. Hover a bar to see all currencies for that month.'
                  : 'Estimated from new subscriber signups + gift orders (USD).'}
              </p>

              {chartData.length === 0 || chartData.every(d => d.amount === 0) ? (
                <p className="text-muted small">No revenue data yet.</p>
              ) : (
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '160px' }}>
                  {chartData.map(({ key, label, amount, currency, count, tooltip }) => (
                    <div key={key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                      <div style={{ fontSize: '10px', color: 'var(--text-muted2)', textAlign: 'center' }}>
                        {amount > 0 ? `${amount.toFixed(0)}` : ''}
                      </div>
                      <div
                        title={hasStripe ? `${label}: ${tooltip}` : `${label}: $${amount.toFixed(2)}`}
                        style={{
                          width: '100%',
                          height: `${Math.max(6, (amount / maxAmount) * 130)}px`,
                          background: amount > 0
                            ? 'linear-gradient(180deg, #a78bfa, #6f42c1)'
                            : '#e5e7eb',
                          borderRadius: '6px 6px 0 0',
                          cursor: 'default',
                        }}
                      />
                      <div style={{ fontSize: '10px', color: 'var(--text-soft)', textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {MONTH_NAMES[parseInt(label.split(' ')[0] === label.split(' ')[0] ? label.split(' ')[0] : 0)]}
                        {label.split(' ')[0]}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Month labels row (cleaner) */}
              <div style={{ display: 'flex', gap: '8px', marginTop: '2px' }}>
                {chartData.map(({ key, label }) => (
                  <div key={key} style={{ flex: 1, textAlign: 'center', fontSize: '10px', color: 'var(--text-soft)' }}>
                    {label.replace(' 20', "'")}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right: health + balance */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>Subscription Health</h6>
              <div className="mb-4">
                <div className="d-flex justify-content-between mb-1">
                  <span style={{ fontSize: '13px', color: 'var(--text-muted2)' }}>Churn this month</span>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: cancelledThisMonth > 0 ? '#dc2626' : '#059669' }}>
                    {cancelledThisMonth} ({churnRate}%)
                  </span>
                </div>
                <div style={{ background: 'var(--header-bg)', borderRadius: '4px', height: '6px' }}>
                  <div style={{ width: `${Math.min(100, churnRate)}%`, background: '#dc2626', height: '6px', borderRadius: '4px' }} />
                </div>
              </div>

              <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>Plan Breakdown (Active)</h6>
              {[
                { plan: 'basic',  label: 'Basic',  count: planBreakdown.basic  || 0, price: P.basicMonthly,  color: '#6f42c1' },
                { plan: 'family', label: 'Family', count: planBreakdown.family || 0, price: P.familyMonthly, color: '#059669' },
                { plan: 'free',   label: 'Free',   count: planBreakdown.free   || 0, price: 0,               color: 'var(--text-muted2)' },
              ].map(({ plan, label, count, price, color }) => (
                <div key={plan} className="d-flex justify-content-between align-items-center mb-2 p-2 rounded-3" style={{ background: 'var(--header-bg)' }}>
                  <span style={{ fontSize: '13px', color, fontWeight: 600 }}>{label}</span>
                  <div className="text-end">
                    <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-main)' }}>{count} users</div>
                    {price > 0 && <div style={{ fontSize: '11px', color: 'var(--text-soft)' }}>~${(count * price).toFixed(0)}/mo est.</div>}
                  </div>
                </div>
              ))}

              <hr className="my-3" />
              <div className="p-3 rounded-3" style={{ background: 'linear-gradient(135deg,#f0e8ff,#e8d5ff)' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted2)', marginBottom: '4px' }}>Total Active Paying</div>
                <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-main)' }}>{activeBasic + activeFamily}</div>
                <div style={{ fontSize: '12px', color: '#6f42c1' }}>subscribers · {deliveredGifts} gift orders</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Currency breakdown (Stripe only) */}
      {hasStripe && Object.keys(stripeRevenue.mrrByCurrency).length > 0 && (
        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
          <div className="card-body p-4">
            <h6 className="fw-bold mb-1" style={{ color: 'var(--text-main)' }}>MRR by Currency</h6>
            <p className="text-muted small mb-3">Real monthly recurring revenue per currency from active Stripe subscriptions</p>
            <div className="row g-2">
              {Object.entries(stripeRevenue.mrrByCurrency)
                .sort((a, b) => b[1] - a[1])
                .map(([currency, amount]) => (
                  <div key={currency} className="col-6 col-md-3 col-lg-2">
                    <div className="p-3 rounded-3 text-center" style={{ background: 'var(--page-bg)', border: '1px solid #ede8ff' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-muted2)' }}>{currency}</div>
                      <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-main)', lineHeight: 1.2 }}>
                        {amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-soft)' }}>/ month</div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Pricing reference */}
      <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
        <div className="card-body p-4">
          <h6 className="fw-bold mb-1" style={{ color: 'var(--text-main)' }}>Pricing Reference</h6>
          <p className="text-muted small mb-4">All prices in USD. Annual saves up to 4 months vs monthly.</p>

          <div className="mb-4">
            <div className="fw-semibold mb-2" style={{ fontSize: '13px', color: 'var(--text-muted2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Subscription Plans</div>
            <div className="row g-2">
              {[
                { name: 'Basic — Monthly',  price: `$${P.basicMonthly}/mo`,                                     sub: 'billed monthly',                          color: '#6f42c1', bg: '#f0ebff', icon: '⭐' },
                { name: 'Basic — Annual',   price: `$${P.basicAnnual}/yr`,                                      sub: `≈ $${(P.basicAnnual/12).toFixed(2)}/mo`,  color: '#6f42c1', bg: '#ede8ff', icon: '⭐' },
                { name: 'Family — Monthly', price: `$${P.familyMonthly}/mo`,                                    sub: 'billed monthly',                          color: '#059669', bg: '#f0fdf4', icon: '👑' },
                { name: 'Family — Annual',  price: `$${P.familyAnnual}/yr`,                                     sub: `≈ $${(P.familyAnnual/12).toFixed(2)}/mo`, color: '#059669', bg: '#dcfce7', icon: '👑' },
                { name: 'Free',             price: '$0',                                                        sub: 'limited stories/mo',                      color: 'var(--text-muted2)', bg: 'var(--header-bg)', icon: '🆓' },
              ].map(p => (
                <div key={p.name} className="col-6 col-md-4 col-lg">
                  <div className="p-3 rounded-3 h-100" style={{ background: p.bg }}>
                    <div style={{ fontSize: '16px', marginBottom: '4px' }}>{p.icon}</div>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: p.color }}>{p.name}</div>
                    <div style={{ fontSize: '17px', fontWeight: 700, color: 'var(--text-main)', lineHeight: 1.2 }}>{p.price}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-soft)', marginTop: '2px' }}>{p.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <div className="fw-semibold mb-2" style={{ fontSize: '13px', color: 'var(--text-muted2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Gift Products</div>
            <div className="row g-2">
              {[
                { name: 'Gift Story',              price: `$${P.gift}`,                            sub: 'one-time, any recipient',   color: '#d97706', bg: '#fffbeb', icon: '🎁' },
                { name: 'Gift Sub — Basic 1mo',    price: `$${P.basicMonthly.toFixed(2)}`,         sub: 'Basic plan, 1 month',       color: '#6f42c1', bg: '#f0ebff', icon: '🎀' },
                { name: 'Gift Sub — Basic 3mo',    price: `$${(P.basicMonthly * 3).toFixed(2)}`,   sub: 'Basic plan, 3 months',      color: '#6f42c1', bg: '#f0ebff', icon: '🎀' },
                { name: 'Gift Sub — Basic 6mo',    price: `$${(P.basicMonthly * 6).toFixed(2)}`,   sub: 'Basic plan, 6 months',      color: '#6f42c1', bg: '#f0ebff', icon: '🎀' },
                { name: 'Gift Sub — Family 1mo',   price: `$${P.familyMonthly.toFixed(2)}`,        sub: 'Family plan, 1 month',      color: '#059669', bg: '#f0fdf4', icon: '🎀' },
                { name: 'Gift Sub — Family 3mo',   price: `$${(P.familyMonthly * 3).toFixed(2)}`,  sub: 'Family plan, 3 months',     color: '#059669', bg: '#f0fdf4', icon: '🎀' },
                { name: 'Gift Sub — Family 6mo',   price: `$${(P.familyMonthly * 6).toFixed(2)}`,  sub: 'Family plan, 6 months',     color: '#059669', bg: '#f0fdf4', icon: '🎀' },
              ].map(p => (
                <div key={p.name} className="col-6 col-md-4 col-lg">
                  <div className="p-3 rounded-3 h-100" style={{ background: p.bg }}>
                    <div style={{ fontSize: '16px', marginBottom: '4px' }}>{p.icon}</div>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: p.color }}>{p.name}</div>
                    <div style={{ fontSize: '17px', fontWeight: 700, color: 'var(--text-main)', lineHeight: 1.2 }}>{p.price}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-soft)', marginTop: '2px' }}>{p.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="fw-semibold mb-2" style={{ fontSize: '13px', color: 'var(--text-muted2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Top-Up Packs (One-Time)</div>
            <div className="row g-2">
              {[
                { name: tp.story?.label     || 'Story Pack',              price: tp.story?.priceDisplay     || '—', sub: tp.story?.description     || '', color: '#7c3aed', bg: '#f5f3ff', icon: tp.story?.emoji     || '📖', who: 'Basic & Family' },
                { name: tp.narration?.label || 'Narration Pack',          price: tp.narration?.priceDisplay || '—', sub: tp.narration?.description || '', color: '#d97706', bg: '#fffbeb', icon: tp.narration?.emoji || '🔊', who: 'Basic & Family' },
                { name: tp.voice?.label     || 'Voice Pack',              price: tp.voice?.priceDisplay     || '—', sub: tp.voice?.description     || '', color: '#db2777', bg: '#fdf2f8', icon: tp.voice?.emoji     || '🎙️', who: 'Family only' },
                { name: tp.regen?.label     || 'Illustration Pack',       price: tp.regen?.priceDisplay     || '—', sub: tp.regen?.description     || '', color: '#059669', bg: '#f0fdf4', icon: tp.regen?.emoji     || '🎨', who: 'Basic & Family' },
                { name: tp.photo?.label     || 'Photo Illustration Pack', price: tp.photo?.priceDisplay     || '—', sub: tp.photo?.description     || '', color: '#db2777', bg: '#fdf2f8', icon: tp.photo?.emoji     || '📸', who: 'All plans' },
              ].map(p => (
                <div key={p.name} className="col-6 col-md-3">
                  <div className="p-3 rounded-3 h-100" style={{ background: p.bg }}>
                    <div style={{ fontSize: '16px', marginBottom: '4px' }}>{p.icon}</div>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: p.color }}>{p.name}</div>
                    <div style={{ fontSize: '17px', fontWeight: 700, color: 'var(--text-main)', lineHeight: 1.2 }}>{p.price}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-soft)', marginTop: '2px' }}>{p.sub}</div>
                    <div style={{ fontSize: '10px', color: p.color, marginTop: '2px', opacity: 0.8 }}>{p.who}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminRevenue;
